import React, { useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './GearCard.module.css';
import { db } from '../../config';
import { doc, updateDoc } from 'firebase/firestore';
import useDragItem from '../../hooks/useDragItem';
import { getItemCategories, matchCatalogItem } from '../../config/helpers';
import { UserContext } from '../../store/user-context';
import HirablePopover from './HirablePopover';

const DragHandleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M4 7H20M4 12H20M4 17H20" stroke="#c8cccc" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21M8 6V4H16V6M19 6L18 20H6L5 6" stroke="#565959" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M9 6L15 12L9 18" stroke="#565959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const MinusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12H19" stroke="#2c3030" strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

// Status → badge pill (label + colour class). 'borrow-pending' is folded
// into 'hired' — the two have always shared one label/style.
const STATUS_META = {
  hire: { label: 'Hirable', className: 'badgeHirable' },
  requested: { label: 'Requested', className: 'badgeRequested' },
  offered: { label: 'Offered', className: 'badgeOffered' },
  hired: { label: 'Hired', className: 'badgeHired' },
};

const QuantityStepper = ({ label, value, onChange }) => (
  <div className={classes.stepper}>
    <div className={classes.stepperLabel}>{label}</div>
    <div className={classes.stepperControls}>
      <button
        className={classes.stepperBtn}
        onClick={() => onChange(Math.max(0, value - 1))}
        aria-label={`Decrease ${label}`}
      >
        <MinusIcon />
      </button>
      <span className={classes.stepperValue}>{value}</span>
      <button
        className={classes.stepperBtn}
        onClick={() => onChange(value + 1)}
        aria-label={`Increase ${label}`}
      >
        <PlusIcon />
      </button>
    </div>
  </div>
);

const GearCard = ({ group, festivalInfo, item, onDelete, onDetail, onShowToast, handleDragStart, handleDragOver, handleDragEnd }) => {
  const UserCtx = useContext(UserContext);
  // Read from existing organise array — Bring = Own, Borrow/Hire = Need, null/unassigned = 1 Own
  const organise = item?.organise || [];
  const bringCount = organise.filter(o => o.method === 'Bring').length;
  const needCount = organise.filter(o => o.method === 'Borrow' || o.method === 'Hire').length;
  const unassignedCount = organise.filter(o => !o.method || o.method === null).length;
  const initialOwn = item?.ownQuantity !== undefined ? item.ownQuantity : bringCount + unassignedCount;
  const initialNeed = item?.needQuantity !== undefined ? item.needQuantity : needCount;
  const [ownQty, setOwnQty] = useState(initialOwn);
  const [needQty, setNeedQty] = useState(initialNeed);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showHirePopover, setShowHirePopover] = useState(false);
  const hireBadgeRef = useRef(null);
  const category = getItemCategories(item)[0];

  const { cardRef, isDragging, isDragOver, dragHandlers, dataAttrs } = useDragItem({
    id: item?.id,
    name: item?.name,
    category,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
  });

  // Only `hire` ("Available for hire") should ever produce the Hirable
  // badge. The badge reflects the item's own catalog hire flag alone — it's
  // a passive label here (no tap action), so a festival with no hireurl of
  // its own (e.g. one using the shared default catalog with no
  // festival-specific hire link yet) still shows Hirable items correctly.
  //
  // isHirable is resolved live from the catalog (festivalInfo's own items,
  // falling back to the shared defaultitems list) rather than trusted from
  // item.hire as stored on the item — same lookup ItemDetail.js already
  // does. The two used to disagree: an item added before a catalog's hire
  // flag was corrected (or seeded from a festival mid-migration to
  // useDefaultItems) keeps whatever hire value it was given at add time,
  // so ItemDetail's fresh catalog lookup could show "Hirable" while this
  // card, trusting the stale stored field, showed nothing.
  // An empty array is still truthy, so a festival with no customised items
  // (useDefaultItems: true and nothing in its own items array) must fall
  // through to the shared catalog explicitly — `[] || UserCtx?.itemInfo`
  // would otherwise short-circuit on the empty array and never reach it.
  const itemInfoList = (festivalInfo?.items?.length ? festivalInfo.items : UserCtx?.itemInfo) || [];
  const catalogMatch = matchCatalogItem(item?.name, itemInfoList);
  const isHirable = catalogMatch?.hire || false;
  const rawStatus = item?.borrowStatus === 'borrow-pending' ? 'hired' : item?.borrowStatus;
  const statusKey = STATUS_META[rawStatus]
    ? rawStatus
    : (isHirable ? 'hire' : null);
  const statusMeta = statusKey ? STATUS_META[statusKey] : null;

  // NOTE: intentionally a plain updateDoc, not a transaction. A transaction
  // was tried here to fix a stale-snapshot race (see below), but Firestore
  // transactions don't participate in the SDK's offline write queue the way
  // a plain updateDoc/setDoc does — attempting one with no connection just
  // fails outright instead of queuing, and never retries once connectivity
  // returns (confirmed: a transactional write attempted offline was still
  // missing from the server 5+ seconds after reconnecting). For a camping/
  // festival app where patchy signal is the norm, that's a much worse
  // failure mode than the race it was meant to fix, so this reverts to
  // updateDoc. The race remains: reading group.items from props and writing
  // the whole array back means two fields on the same card (Own then Need),
  // or two different cards, tapped within the same round-trip can build
  // their update from the same stale snapshot, so whichever write reaches
  // Firestore last wins the whole document — silently discarding the other
  // change with no error. Rare in practice (requires two taps within one
  // round-trip) and self-correcting on the next successful save, which is
  // preferable to losing writes outright when offline.
  const updateQuantity = async (field, value) => {
    if (!group?.id || !item?.id) return;
    setSaving(true);
    try {
      const itemIndex = group.items.findIndex(i => i.id === item.id);
      if (itemIndex === -1) return;
      const updatedItems = [...group.items];
      const current = updatedItems[itemIndex];
      if (field === 'ownQuantity' && value === 0) {
        // Own dropping to 0 means this item isn't being borrowed from
        // whoever lent it anymore (or the user decided not to bring it
        // after all) — clear lentBy so it doesn't silently reappear as
        // "Borrowed from <name>" in Pack if Own gets raised again on this
        // same item later. Deleted, not nulled, matching the clean-field
        // pattern used elsewhere (borrowStatus/offeredBy in
        // commitMoveToPack/handleGotIt/handleClearStatus — untouched,
        // separate write paths from this stepper). Scoped tightly: only
        // ownQuantity landing on exactly 0, never needQuantity changes or
        // ownQuantity moving between nonzero values.
        const { lentBy, ...rest } = current;
        updatedItems[itemIndex] = { ...rest, [field]: value };
      } else {
        updatedItems[itemIndex] = { ...current, [field]: value };
      }
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, { items: updatedItems });
    } catch (err) {
      console.log('Error updating quantity:', err);
      onShowToast && onShowToast("Couldn't save that change — check your connection and try again.");
    }
    setSaving(false);
  };

  const handleOwnChange = (val) => {
    if (val > ownQty) onShowToast && onShowToast('Added to your pack list ✓');
    setOwnQty(val);
    updateQuantity('ownQuantity', val);
  };

  const handleNeedChange = (val) => {
    if (val > needQty) onShowToast && onShowToast('Added to your need list ✓');
    setNeedQty(val);
    updateQuantity('needQuantity', val);
  };

  const deleteItem = async () => {
    if (!group?.id || !item?.id) return;
    setSaving(true);
    try {
      const updatedItems = group.items.filter(i => i.id !== item.id);
      const groupRef = doc(db, 'groups', group.id);
      await updateDoc(groupRef, { items: updatedItems });
    } catch (err) {
      console.log('Error deleting item:', err);
      onShowToast && onShowToast("Couldn't save that change — check your connection and try again.");
    }
    setSaving(false);
    setConfirmDelete(false);
  };

  if (!item) return null;

  return (
    <>
    <div
      ref={cardRef}
      {...dataAttrs}
      className={`${classes.card} ${isDragging ? classes.cardDragging : ''} ${isDragOver ? classes.cardDragOver : ''}`}
      {...dragHandlers}
    >

      {/* Item name row */}
      <div className={classes.nameRow}>
        {handleDragStart && (
          <span className={classes.dragHandle} aria-hidden="true">
            <DragHandleIcon />
          </span>
        )}
        <span className={classes.itemName}>
          {item.name}
          {(ownQty + needQty) > 1 && (
            <span className={classes.itemQty}> ×{ownQty + needQty}</span>
          )}
        </span>
        {statusMeta && (
          statusKey === 'hire' ? (
            <button
              ref={hireBadgeRef}
              className={`${classes.badge} ${classes[statusMeta.className]} ${classes.badgeTappable}`}
              onClick={() => setShowHirePopover(v => !v)}
            >
              {statusMeta.label}
            </button>
          ) : (
            <span className={`${classes.badge} ${classes[statusMeta.className]}`}>
              {statusMeta.label}
            </span>
          )
        )}
        {showHirePopover && (
          <HirablePopover
            anchorRef={hireBadgeRef}
            festivalName={festivalInfo?.name || group?.festival?.name}
            hireUrl={festivalInfo?.hireurl}
            onClose={() => setShowHirePopover(false)}
          />
        )}
        <div className={classes.spacer} />
        <div className={classes.actions}>
          <button
            className={classes.actionBtn}
            onClick={() => setConfirmDelete(true)}
            aria-label="Delete item"
          >
            <DeleteIcon />
          </button>
          <button
            className={classes.actionBtn}
            onClick={() => onDetail && onDetail(item)}
            aria-label="Item details"
          >
            <ChevronIcon />
          </button>
        </div>
      </div>

      {/* Steppers row */}
      <div className={classes.steppersRow}>
        <QuantityStepper
          label="Own"
          value={ownQty}
          onChange={handleOwnChange}
        />
        <QuantityStepper
          label="Need"
          value={needQty}
          onChange={handleNeedChange}
        />
      </div>

    </div>
    {confirmDelete && <ConfirmDialog itemName={item.name} onCancel={() => setConfirmDelete(false)} onConfirm={deleteItem} saving={saving}/>}
    </>
  );
};

const ConfirmDialog = ({ itemName, onCancel, onConfirm, saving }) => {
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(44,48,48,0.55)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px'
    }}>
      <div style={{
        backgroundColor: '#ffffff', borderRadius: '20px', padding: '28px 24px',
        width: '100%', maxWidth: '300px', display: 'flex', flexDirection: 'column',
        gap: '20px', boxShadow: '0 8px 32px rgba(44,48,48,0.18)'
      }}>
        <p style={{
          fontFamily: 'Poppins, Arial, sans-serif', fontSize: '15px',
          color: '#2c3030', textAlign: 'center', lineHeight: '1.5', margin: 0
        }}>Remove <strong>{itemName}</strong> from your list?</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} style={{
            flex: 1, height: '48px', borderRadius: '99px', backgroundColor: '#f5f7f7',
            border: '1px solid #e0e1e1', fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: '14px', fontWeight: 600, color: '#565959', cursor: 'pointer'
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={saving} style={{
            flex: 1, height: '48px', borderRadius: '99px', backgroundColor: '#fc4b4e',
            border: 'none', fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: '14px', fontWeight: 600, color: '#ffffff', cursor: 'pointer',
            opacity: saving ? 0.6 : 1
          }}>{saving ? 'Removing...' : 'Remove'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GearCard;
